import React from "react";
import Button from "../../../components/buttons";
import ThemedModal from "../../../components/utilities/ThemedModal";
import { Modal } from "react-bootstrap";
import { ModalConfirmation } from "../../../types/DefModalOptions";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { CenterFormColumnComponent } from "../../../components/utilities";
import { ReviseTransactionDetailsRequest } from "../hooks/useTransactionDetails";
import { DateInput, CurrencyInput, NumberInput, FormLabel } from "../../../components/forms";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";

export type ReviseFundModalForm = { transactionDate: string | null, quantity: number, value: number };
export type ReviseFundModalProps = ModalConfirmation<ReviseTransactionDetailsRequest> & {
    asset: any
};

const ReviseFundModal = ({
    asset,
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<ReviseFundModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const formMethods = useForm<ReviseFundModalForm>();
    const { handleSubmit, reset } = formMethods;

    const onClose = () => cancel!();

    const onSubmit = (form: ReviseFundModalForm) => {
        if (!defaultValues) {
            return;
        }
        proceed!({
            ...defaultValues,
            transactionDate: form.transactionDate,
            quantity: form.quantity,
            value: form.value
        });
    };

    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset({ 
            transactionDate: defaultValues.transactionDate,
            quantity: defaultValues.quantity,
            value: defaultValues.value
        });
    }, [ defaultValues ]);

    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Revise</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body ref={bodyAnimation}>
                        <ReviseFundsModalContent asset={asset} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="success" type="submit">Submit</Button>
                        <Button variant="light" type="button" onClick={onClose}>Cancel</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );
};

const ReviseFundsModalContent = ({ asset }: Readonly<{ asset: any }>) => {
    const { control, setValue } = useFormContext<ReviseFundModalForm>();
    return (
        <React.Fragment>
            <div className="row gy-2">
                <div className="col-3">
                    <CenterFormColumnComponent className="col-md-2">
                        <FormLabel>{asset?.asset?.longAssetName}</FormLabel>
                    </CenterFormColumnComponent>
                </div>
                <div className="col-3">
                    <Controller
                        name="transactionDate"
                        control={control}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <DateInput
                                label="Date"
                                disableAnimations
                                isClearable={true}
                                value={value}
                                onBlur={(v: string | null) => setValue('transactionDate', v, { shouldDirty: true })}
                            />
                        )}
                    />
                </div>
                <div className="col-3">
                    <Controller
                        name="value"
                        control={control}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <CurrencyInput
                                label="Value"
                                disableAnimations
                                value={value}
                                decimalScale={2}
                                onBlur={(value: any) => setValue('value', value, { shouldDirty: true })}
                                
                            />
                        )}
                    />
                </div>
                <div className="col-3">
                    <Controller
                        name="quantity"
                        control={control}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <NumberInput
                                label="Quantity"
                                disableAnimations
                                thousandSeparator
                                decimalScale={5}
                                value={value}
                                onBlur={(value: any) => setValue("quantity", value, { shouldDirty: true })}
                            />
                        )}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export function openReviseFundModal(baseRequest: ReviseTransactionDetailsRequest, asset: any) {
    return createConfirmation<ReviseFundModalProps, ReviseTransactionDetailsRequest>(confirmable(ReviseFundModal))({
        dialogId: 'revise_fund_modal',
        defaultValues: baseRequest,
        asset
    });
}
